import * as React from "react";
import { useOrgHint } from "@abodeminehq/entitlements-client-react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useOrganizationCache } from "~/lib/queries/hooks";
import { useOrganization } from "~/lib/queries/organizations";
import type { Organization } from "~/lib/schemas";
import { useAuthContext } from "./auth-context";
import { isAdminMode as getIsAdminMode, setAdminMode } from "~/lib/admin-mode";
import { LogtoScopes } from "~/lib/logto/logto";
import { testScopes } from "~/lib/scopes";

interface OrgContextType {
  activeOrgId: string | null;
  activeOrg: Organization | null;
  setActiveOrgId: (orgId: string | null) => void;
  isOnOrgRoute: boolean;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  lastUsedOrgId: string | null;
  isAdminMode: boolean;
}

const OrgContext = React.createContext<OrgContextType | undefined>(undefined);

export const LAST_USED_ORG_KEY = "lastUsedOrgId";

function parseOrgIdFromPath(pathname: string): { orgId: string | null; isAdminRoute: boolean } {
  const adminOrgMatch = pathname.match(/^\/admin\/org\/([^\/]+)(?:\/.*)?$/);
  if (adminOrgMatch) {
    return { orgId: adminOrgMatch[1], isAdminRoute: true };
  }
  const orgMatch = pathname.match(/^\/org\/([^\/]+)(?:\/.*)?$/);
  if (orgMatch) {
    return { orgId: orgMatch[1], isAdminRoute: false };
  }
  return { orgId: null, isAdminRoute: false };
}

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const [activeOrgId, setActiveOrgId] = React.useState<string | null>(null);
  const [adminMode, setAdminModeState] = React.useState(() => getIsAdminMode());
  const location = useLocation();
  const navigate = useNavigate();

  const { setActiveOrg: setActiveAuthOrg, claims } = useAuthContext();

  const getLastUsedOrgId = React.useCallback(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(LAST_USED_ORG_KEY);
  }, []);

  const [lastUsedOrgId, setLastUsedOrgId] = React.useState<string | null>(
    getLastUsedOrgId(),
  );

  const pendingSwitchRef = React.useRef<string | null>(null);
  const switchVersionRef = React.useRef(0);

  // `?org=<id>` — the hint another product's app switcher puts on the link that
  // sent this person here, so they arrive in the org they just picked rather
  // than in whatever this app last remembered.
  //
  // Here the active org IS the route, so honouring the hint means navigating to
  // it; the effect below then does everything else it already does for an org
  // route. It is applied signed out too — /org/$org sends an unauthenticated
  // visitor through sign-in and back to this same URL, so the org survives the
  // round trip without being stored anywhere first.
  useOrgHint((orgId) => {
    const { orgId: current } = parseOrgIdFromPath(location.pathname);
    if (current === orgId) return;
    navigate({ to: "/org/$org", params: { org: orgId }, replace: true });
  });

  // Verify admin mode whenever auth claims change
  React.useEffect(() => {
    if (getIsAdminMode() && claims) {
      const hasAdminScope = testScopes(claims.scopes || [], LogtoScopes.AdminAccess);
      if (!hasAdminScope) {
        setAdminMode(false);
        setAdminModeState(false);
      }
    }
  }, [claims]);

  // Sync admin mode from session storage on route change
  React.useEffect(() => {
    setAdminModeState(getIsAdminMode());
  }, [location.pathname]);

  React.useEffect(() => {
    const { orgId, isAdminRoute } = parseOrgIdFromPath(location.pathname);
    const version = ++switchVersionRef.current;
    const inAdminMode = isAdminRoute || getIsAdminMode();

    setActiveOrgId(orgId);

    if (inAdminMode) {
      pendingSwitchRef.current = null;
      setActiveAuthOrg(null);
      return;
    }

    if (location.pathname.startsWith("/org")) {
      if (orgId === pendingSwitchRef.current) {
        return;
      }
      pendingSwitchRef.current = orgId;

      setActiveAuthOrg(orgId).then(() => {
        if (version !== switchVersionRef.current) return;
        pendingSwitchRef.current = null;
      });

      if (orgId) {
        localStorage.setItem(LAST_USED_ORG_KEY, orgId);
        setLastUsedOrgId(orgId);
      }
    }
  }, [location.pathname, setActiveAuthOrg]);

  const {
    data: activeOrg,
    isLoading,
    error,
    refetch,
  } = useOrganization(activeOrgId, {
    enabled: Boolean(activeOrgId),
    staleTime: 60 * 60 * 1000,
  });

  const cachedOrg = useOrganizationCache(activeOrgId);
  const orgData = activeOrg || cachedOrg || null;

  const isOnOrgRoute = Boolean(activeOrgId);

  const value = React.useMemo(
    () => ({
      activeOrgId,
      activeOrg: orgData,
      setActiveOrgId,
      isOnOrgRoute,
      isLoading,
      error,
      refetch,
      lastUsedOrgId,
      isAdminMode: adminMode,
    }),
    [
      activeOrgId,
      orgData,
      isOnOrgRoute,
      isLoading,
      error,
      refetch,
      lastUsedOrgId,
      adminMode,
    ],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg() {
  const context = React.useContext(OrgContext);
  if (context === undefined) {
    throw new Error("useOrg must be used within an OrgProvider");
  }
  return context;
}
